import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ThemeService, ThemePreference } from '../../services/theme.service';

interface ThemeOption {
  value: ThemePreference;
  label: string;
  icon: string;
}

/**
 * Controle de tema segmentado (claro / escuro / sistema), inline no header.
 * Pílula discreta que se funde à superfície do header; segmentos de largura igual,
 * ícones centrados. Acessível: grupo de botões com aria-pressed e rótulos.
 */
@Component({
  selector: 'app-theme-switcher',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="theme" role="group" aria-label="Tema">
      <button
        *ngFor="let opt of options"
        type="button"
        class="theme__seg"
        [class.theme__seg--active]="current === opt.value"
        [attr.aria-pressed]="current === opt.value"
        [attr.aria-label]="opt.label"
        [title]="opt.label"
        (click)="select(opt.value)">
        <i class="fas" [ngClass]="opt.icon" aria-hidden="true"></i>
      </button>
    </div>
  `,
  styles: [`
    .theme {
      display: inline-flex;
      align-items: center;
      height: 2.25rem;             /* 36px — alinha com ng-select, status e avatar */
      padding: 3px;
      gap: 2px;
      border: 1px solid var(--border);
      border-radius: 9999px;       /* pílula */
      background: var(--surface);  /* funde com o header */
      box-sizing: border-box;
    }
    .theme__seg {
      display: grid;
      place-items: center;         /* centra o glifo com precisão */
      width: 1.75rem;              /* 28px — todos iguais */
      height: 100%;
      padding: 0;
      border: 0;
      border-radius: 9999px;
      background: transparent;
      color: var(--text-muted);
      cursor: pointer;
      font-size: .75rem;           /* 12px — ícones do mesmo tamanho */
      line-height: 1;
      transition: background-color .15s ease, color .15s ease;
    }
    .theme__seg i { display: block; width: 1em; text-align: center; }
    .theme__seg:hover { color: var(--text-primary); background: var(--surface-muted); }
    .theme__seg:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }
    .theme__seg--active {
      color: #ffffff;              /* ícone sobre o acento — legível nos 2 temas */
      background: var(--brand);
    }
    .theme__seg--active:hover { color: #ffffff; background: var(--brand); }
  `],
})
export class ThemeSwitcherComponent {
  readonly options: ReadonlyArray<ThemeOption> = [
    { value: 'light', label: 'Tema claro', icon: 'fa-sun' },
    { value: 'dark', label: 'Tema escuro', icon: 'fa-moon' },
    { value: 'system', label: 'Tema do sistema', icon: 'fa-desktop' },
  ];

  constructor(private readonly themeService: ThemeService) {}

  get current(): ThemePreference {
    return this.themeService.preference;
  }

  select(value: ThemePreference): void {
    this.themeService.setPreference(value);
  }
}
